<template>
  <SalesPlayDrawerCard>
    <div class="drawer-section">
      <div class="drawer-sectionLabel">REPORT</div>
      <div class="drawer-mutedText">
        The report must be based on Account, Contact, or Lead, and must
        include a Website column (Account) or an Email column (Contact/Lead).
      </div>
      <SearchSelect
        :model-value="localReportId"
        :options="initialOption"
        :options-function="searchReports"
        placeholder="Choose a Salesforce report..."
        search-placeholder="Search reports by name..."
        :allow-clear="false"
        @update:model-value="onReportSelected"
      />
      <div v-if="validating" class="drawer-mutedText">Checking report…</div>
      <div v-else-if="validationError" class="drawer-errorText">{{ validationError }}</div>
      <div v-else-if="validationSuccess" class="drawer-successText">{{ validationSuccess }}</div>
    </div>

  </SalesPlayDrawerCard>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import SalesPlayDrawerCard from './SalesPlayDrawerCard.vue'
import SearchSelect from '../../../components/SearchSelect.vue'
import { apiFetch } from '../../../services/api'
import { useSalesPlayDraft } from '../../composables/useSalesPlayDraft'
import { useBlockValidation } from '../../composables/useBlockValidation'

const props = defineProps<{
  playId: string
  pipelineId: string | null
  nodeIndex: number
}>()

const { nodes, updateNodeConfig } = useSalesPlayDraft()
const { validating, validationError, validationSuccess, validate: runValidation, reset: resetValidation } = useBlockValidation()

// ── Local working copy ──────────────────────────────────────────

const currentConfig = () => nodes.value[props.nodeIndex]?.config || {}

const localReportId = ref(currentConfig().report_id || '')
const localReportName = ref(currentConfig().report_name || '')

function resetLocal() {
  const cfg = currentConfig()
  localReportId.value = cfg.report_id || ''
  localReportName.value = cfg.report_name || ''
  resetValidation()
}

// Re-sync when the draft node changes (e.g. after canvas-level cancel).
watch(() => nodes.value[props.nodeIndex]?.config, () => {
  resetLocal()
}, { deep: true })

// Provide the current selection so SearchSelect can show name + ID at rest.
const initialOption = computed(() => {
  if (!localReportId.value) return []
  return [{
    value: localReportId.value,
    label: localReportName.value || localReportId.value,
    secondaryLabel: localReportId.value,
  }]
})

// ── Report search via SearchSelect ──────────────────────────────

let lastFetchedOptions: { value: string; label: string }[] = []

async function searchReports(query: string) {
  const qs = query ? `?search=${encodeURIComponent(query)}` : ''
  const resp: any = await apiFetch(`/integrations/salesforce/reports${qs}`)
  const reports = resp?.reports || []
  const opts = reports.map((r: any) => ({
    value: r.id,
    label: r.name,
    secondaryLabel: r.id,
  }))
  lastFetchedOptions = opts
  return opts
}

function onReportSelected(reportId: string) {
  if (!reportId) return
  localReportId.value = reportId
  const match = lastFetchedOptions.find((o) => o.value === reportId)
  if (match) localReportName.value = match.label
  runValidation('sfdc_report', { type: 'sfdc_report', report_id: reportId, report_name: localReportName.value })
  const currentName = currentConfig().name
  updateNodeConfig(props.nodeIndex, {
    type: 'sfdc_report',
    name: currentName || localReportName.value,
    report_id: localReportId.value,
    report_name: localReportName.value,
  })
}
</script>

